import type { Continuity } from '@dissidium/contracts';
import { validateLedger } from '../continuity/ledger.ts';

export const LOCAL_EVIDENCE_KINDS = ['document', 'physical-remains', 'living-testimony', 'lawful-demonstration'] as const;
export const PROTECTION_KINDS = ['safe-conduct', 'relocation', 'public-custody', 'debt-release'] as const;

export type LocalEvidenceKind = typeof LOCAL_EVIDENCE_KINDS[number];
export type WitnessProtectionKind = typeof PROTECTION_KINDS[number];

export type LocalEvidence = Readonly<{
  id: string;
  caseId: string;
  kind: LocalEvidenceKind;
  sourceKey: string;
  finding: string;
  verified: boolean;
  witnessId: string | null;
}>;

export type WitnessProtection = Readonly<{
  id: string;
  witnessId: string;
  kind: WitnessProtectionKind;
  guarantorId: string;
  commitments: readonly string[];
  secured: boolean;
}>;

export type RejectedEvidence = Readonly<{
  evidenceId: string;
  reason: 'wrong-case' | 'unverified' | 'duplicate-source' | 'witness-unavailable' | 'witness-unprotected';
}>;

export type HearingAssessment = Readonly<{
  caseId: string;
  credible: boolean;
  requiredIndependentTypes: 2;
  independentTypes: readonly LocalEvidenceKind[];
  acceptedEvidenceIds: readonly string[];
  protectedWitnessIds: readonly string[];
  rejected: readonly RejectedEvidence[];
}>;

const text = (value: unknown, label: string): void => {
  if (typeof value !== 'string' || value.trim() === '') throw new Error(`${label} must be non-empty text`);
};

function validateEvidence(evidence: LocalEvidence): void {
  text(evidence.id, 'Evidence id');
  text(evidence.caseId, 'Evidence case id');
  text(evidence.sourceKey, 'Evidence source key');
  text(evidence.finding, 'Evidence finding');
  if (!LOCAL_EVIDENCE_KINDS.includes(evidence.kind)) throw new Error(`Unknown local evidence kind ${evidence.kind}`);
  if (typeof evidence.verified !== 'boolean') throw new Error('Evidence verification must be explicit');
  if (evidence.kind === 'living-testimony') text(evidence.witnessId, 'Living testimony witness id');
  else if (evidence.witnessId !== null) throw new Error('Only living testimony carries a witness id');
}

function validateProtection(protection: WitnessProtection): void {
  text(protection.id, 'Witness protection id');
  text(protection.witnessId, 'Protected witness id');
  text(protection.guarantorId, 'Witness protection guarantor');
  if (!PROTECTION_KINDS.includes(protection.kind)) throw new Error(`Unknown witness protection kind ${protection.kind}`);
  if (typeof protection.secured !== 'boolean') throw new Error('Witness protection status must be explicit');
  if (!Array.isArray(protection.commitments) || protection.commitments.length === 0) throw new Error('Witness protection needs a material commitment');
  for (const commitment of protection.commitments) text(commitment, 'Witness protection commitment');
}

/**
 * A credible Silt Stair hearing needs two different kinds of corroboration.
 * Copies sharing one source key count once, and live witnesses need real safety.
 */
export function assessLocalHearing(
  input: Continuity,
  caseId: string,
  evidence: readonly LocalEvidence[],
  protections: readonly WitnessProtection[],
): HearingAssessment {
  const c = validateLedger(input);
  text(caseId, 'Hearing case id');
  for (const record of evidence) validateEvidence(record);
  for (const protection of protections) validateProtection(protection);
  if (new Set(evidence.map(record => record.id)).size !== evidence.length) throw new Error('Evidence ids must be unique');
  if (new Set(protections.map(protection => protection.id)).size !== protections.length) throw new Error('Witness protection ids must be unique');

  const accepted: LocalEvidence[] = [];
  const rejected: RejectedEvidence[] = [];
  const sourceKeys = new Set<string>();
  const protectedWitnessIds = new Set<string>();

  for (const record of [...evidence].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0)) {
    if (record.caseId !== caseId) {
      rejected.push({ evidenceId: record.id, reason: 'wrong-case' });
      continue;
    }
    if (!record.verified) {
      rejected.push({ evidenceId: record.id, reason: 'unverified' });
      continue;
    }
    if (sourceKeys.has(record.sourceKey)) {
      rejected.push({ evidenceId: record.id, reason: 'duplicate-source' });
      continue;
    }
    if (record.kind === 'living-testimony') {
      const witness = c.persons[record.witnessId!];
      if (!witness || witness.lifecycle !== 'active' || !witness.location) {
        rejected.push({ evidenceId: record.id, reason: 'witness-unavailable' });
        continue;
      }
      const protection = protections.find(plan => plan.witnessId === record.witnessId && plan.secured);
      if (!protection) {
        rejected.push({ evidenceId: record.id, reason: 'witness-unprotected' });
        continue;
      }
      protectedWitnessIds.add(record.witnessId!);
    }
    sourceKeys.add(record.sourceKey);
    accepted.push(record);
  }

  const independentTypes = [...new Set(accepted.map(record => record.kind))].sort();
  return {
    caseId,
    credible: independentTypes.length >= 2,
    requiredIndependentTypes: 2,
    independentTypes,
    acceptedEvidenceIds: accepted.map(record => record.id),
    protectedWitnessIds: [...protectedWitnessIds].sort(),
    rejected,
  };
}
